import { useCallback, useMemo } from 'react';

import type { Test } from '@bugbug/core/types/tests';
import { useAppSelector } from '~/modules/store';
import { ACTIONS } from '~/services/toasts/internalServerError';
import { useTestRunnerActionsState } from '~/views/TestDetails/components/TestActions/TestHeaderActions.hooks';

import { testsApi } from '../../tests.api';
import { selectCurrentTestId } from '../../tests.selectors';
import { showTestError } from '../../tests.utils';

export const useUpdateCurrentTestProfile = () => {
  const [updateTest, updateTestProfileRequest] = testsApi.useUpdateTestMutation();
  const { setPendingAction, resetPendingAction } = useTestRunnerActionsState();
  const currentTestId = useAppSelector(selectCurrentTestId);

  const handleUpdateTestProfile = useCallback(
    async (profileId: Test['runProfileId']) => {
      if (!currentTestId) return null;

      setPendingAction('updateProfile');

      const result = updateTest({
        id: currentTestId,
        data: {
          runProfileId: profileId,
        },
      }).unwrap();

      result
        .catch((error) => {
          showTestError(ACTIONS.UPDATE, error as Error);
        })
        .finally(() => {
          resetPendingAction();
        });

      return result;
    },
    [currentTestId, setPendingAction, updateTest, resetPendingAction],
  );

  return useMemo(
    () => [handleUpdateTestProfile, updateTestProfileRequest] as const,
    [handleUpdateTestProfile, updateTestProfileRequest],
  );
};
